import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import aiDevelopmentService from "../../services/aiDevelopmentService";
import { AI_WORKFLOW_STATUSES } from "./AiWorkflowProgress";
import AiStoryForm, { EMPTY_STORY_FORM, storyPayload, storyToForm } from "./AiStoryForm";
import AiDevelopmentKanbanBoard from "./AiDevelopmentKanbanBoard";
import "./AiDevelopmentStudio.css";

export default function AiDevelopmentStoriesPage() {
  const { t } = useTranslation();
  const [result, setResult] = useState({ content: [], totalPages: 0 });
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [status, setStatus] = useState("");
  const [view, setView] = useState("list");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_STORY_FORM);

  const load = useCallback(
    async (nextPage = 0) => {
      setLoading(true);
      setError("");
      try {
        const [stories, projectRows] = await Promise.all([
          aiDevelopmentService.stories(status, nextPage, 25, projectId),
          aiDevelopmentService.projects(),
        ]);
        setResult(stories || { content: [], totalPages: 0 });
        setProjects(projectRows || []);
        setPage(nextPage);
      } catch (e) {
        setError(e?.message || t("common.errorOccurred"));
      } finally {
        setLoading(false);
      }
    },
    [status, projectId, t],
  );
  useEffect(() => {
    load(0);
  }, [status, projectId, load]);

  const closeForm = () => {
    setForm(EMPTY_STORY_FORM);
    setEditingId(null);
    setShowForm(false);
  };
  const save = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = storyPayload(form);
      if (editingId) await aiDevelopmentService.updateStory(editingId, payload);
      else await aiDevelopmentService.createStory(payload);
      closeForm();
      await load(0);
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
      setLoading(false);
    }
  };
  const create = () => {
    setEditingId(null);
    setForm(EMPTY_STORY_FORM);
    setShowForm(true);
  };
  const edit = (row) => {
    setEditingId(row.id);
    setForm(storyToForm(row));
    setShowForm(true);
  };

  return (
    <div className="ai-studio-page">
      <header className="ai-studio-header">
        <div>
          <h1>{t("aiDevelopment.title")}</h1>
          <p>{t("aiDevelopment.subtitle")}</p>
        </div>
        <button className="btn btn-primary" onClick={create}>
          {t("aiDevelopment.newStory")}
        </button>
      </header>
      {error && <div className="ai-studio-alert">{error}</div>}
      {showForm && (
        <AiStoryForm
          form={form}
          setForm={setForm}
          projects={projects}
          loading={loading}
          onSubmit={save}
          onCancel={closeForm}
          title={t(editingId ? "aiDevelopment.editStory" : "aiDevelopment.createTitle")}
          submitLabel={t(editingId ? "common.save" : "common.create")}
          t={t}
          storyId={editingId}
        />
      )}
      <section className="ai-studio-panel">
        <div className="ai-toolbar">
          <label>
            {t("aiDevelopment.project")}
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">{t("common.all")}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          {view === "list" && (
            <label>
              {t("aiDevelopment.status")}
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">{t("common.all")}</option>
                {AI_WORKFLOW_STATUSES.map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
            </label>
          )}
          <div className="ai-actions">
            <button
              type="button"
              className={`btn ${view === "list" ? "btn-primary" : "btn-default"}`}
              aria-pressed={view === "list"}
              onClick={() => setView("list")}
            >
              {t("aiDevelopment.listView")}
            </button>
            <button
              type="button"
              className={`btn ${view === "kanban" ? "btn-primary" : "btn-default"}`}
              aria-pressed={view === "kanban"}
              onClick={() => setView("kanban")}
            >
              {t("aiDevelopment.kanbanView")}
            </button>
          </div>
          {view === "list" && (
            <button className="btn btn-default" onClick={() => load(page)}>
              {t("aiDevelopment.refresh")}
            </button>
          )}
        </div>

        {view === "kanban" ? (
          <AiDevelopmentKanbanBoard projectId={projectId} t={t} />
        ) : (
          <>
            <div className="ai-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("aiDevelopment.reference")}</th>
                    <th>{t("aiDevelopment.storyTitle")}</th>
                    <th>{t("aiDevelopment.project")}</th>
                    <th>{t("aiDevelopment.status")}</th>
                    <th>{t("aiDevelopment.priority")}</th>
                    <th>{t("aiDevelopment.updated")}</th>
                    <th>{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7">{t("common.loading")}</td>
                    </tr>
                  ) : !result.content?.length ? (
                    <tr>
                      <td colSpan="7">{t("common.noData")}</td>
                    </tr>
                  ) : (
                    result.content.map((row) => (
                      <tr key={row.id}>
                        <td>{row.externalReference || "—"}</td>
                        <td>
                          <Link to={`/ai-development/stories/${row.id}`}>{row.title}</Link>
                        </td>
                        <td>{row.projectName || row.projectId}</td>
                        <td>
                          <span className="ai-status">{row.status}</span>
                        </td>
                        <td>{row.priority}</td>
                        <td>{new Date(row.updatedAt).toLocaleString()}</td>
                        <td>
                          {row.status === "DRAFT" && (
                            <button className="btn btn-default" onClick={() => edit(row)}>
                              {t("common.edit")}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="ai-pagination">
              <button disabled={page === 0} onClick={() => load(page - 1)}>
                ‹
              </button>
              <span>
                {page + 1} / {Math.max(result.totalPages || 1, 1)}
              </span>
              <button disabled={page + 1 >= result.totalPages} onClick={() => load(page + 1)}>
                ›
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
